import React, { useEffect, useMemo, useState } from 'react';
import { Container } from 'react-bootstrap';
import '../fichierCss/Navigation.css';
import '../fichierCss/Connexion.css';
import AuthentificationApi from '../api/AuthentificationApi';
import { effacerRoleCourant } from '../utils/auth';
import FormulaireConnexion from './formulaires/FormulaireConnexion';
import FormulaireInscription from './formulaires/FormulaireInscription';
import FormulaireMotDePasseOublie from './formulaires/FormulaireMotDePasseOublie';
import FormulaireReinitialiserMotDePasse from './formulaires/FormulaireReinitialiserMotDePasse';

const Connexion = () => {
  const [formulaireOuvert, setFormulaireOuvert] = useState(false);
  const [inscriptionOuverte, setInscriptionOuverte] = useState(false);
  const [motDePasseOublieOuvert, setMotDePasseOublieOuvert] = useState(false);
  const [reinitialiserMotDePasseOuvert, setReinitialiserMotDePasseOuvert] = useState(false);
  const [tokenReinitialisation, setTokenReinitialisation] = useState('');
  const [deconnexionEnCours, setDeconnexionEnCours] = useState(false);
  const [estConnecte, setEstConnecte] = useState(() => Boolean(localStorage.getItem('accessToken')));
  const [courrielUtilisateur, setCourrielUtilisateur] = useState(() => localStorage.getItem('userCourriel') || '');

  const synchroniserEtatConnexion = () => {
    setEstConnecte(Boolean(localStorage.getItem('accessToken')));
    setCourrielUtilisateur(localStorage.getItem('userCourriel') || '');
  };

  useEffect(() => {
    const gererStockage = () => {
      synchroniserEtatConnexion();
    };

    const gererAuthChange = () => {
      synchroniserEtatConnexion();
    };

    // Extraire le token de réinitialisation de l'URL si présent
    const paramUrl = new URLSearchParams(window.location.search);
    const tokenUrl = paramUrl.get('reset-token');
    if (tokenUrl) {
      setTokenReinitialisation(tokenUrl);
      setReinitialiserMotDePasseOuvert(true);
    }

    window.addEventListener('storage', gererStockage);
    window.addEventListener('auth-state-changed', gererAuthChange);

    return () => {
      window.removeEventListener('storage', gererStockage);
      window.removeEventListener('auth-state-changed', gererAuthChange);
    };
  }, []);

  const etiquetteEtat = useMemo(() => (estConnecte ? 'En ligne' : 'Hors ligne'), [estConnecte]);

  const effacerSessionLocale = () => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('userCourriel');
    effacerRoleCourant();
    sessionStorage.removeItem('accessToken');
    sessionStorage.removeItem('refreshToken');
    sessionStorage.removeItem('userCourriel');
    setFormulaireOuvert(false);
    setInscriptionOuverte(false);
    synchroniserEtatConnexion();
    window.dispatchEvent(new Event('auth-state-changed'));
  };

  const gererDeconnexion = async () => {
    if (deconnexionEnCours) {
      return;
    }

    setDeconnexionEnCours(true);

    try {
      const refreshToken = localStorage.getItem('refreshToken');
      const accessToken = localStorage.getItem('accessToken');

      // Deconnexion locale immediate pour eviter un blocage UI si l'API est lente.
      effacerSessionLocale();

      if (refreshToken && accessToken) {
        await Promise.race([
          AuthentificationApi.deconnecter(refreshToken, accessToken),
          new Promise((_, reject) => {
            setTimeout(() => reject(new Error('Timeout de deconnexion')), 4000);
          })
        ]);
      }
    } catch (exception) {
      // La session locale est deja nettoyee meme en cas d'erreur reseau.
    } finally {
      setDeconnexionEnCours(false);
    }
  };

  return (
    <>
      <section className="auth-toolbar-wrapper mb-3">
        <Container fluid>
          <div className="auth-toolbar" role="region" aria-label="Barre de connexion">
            <div className="auth-toolbar-brand-block">
              <span className="auth-toolbar-kicker">Espace membre</span>
              <h3 className="auth-toolbar-title">Bienvenue sur votre portail</h3>
            </div>

            <div className="auth-toolbar-right">
              <div className={`auth-status-pill ${estConnecte ? 'connecte' : 'hors-ligne'}`}>
                <span className="auth-status-dot" aria-hidden="true" />
                {estConnecte ? (
                  <span>{courrielUtilisateur}</span>
                ) : (
                  <span>{etiquetteEtat}</span>
                )}
              </div>

              {estConnecte ? (
                <button
                  type="button"
                  className="connexion-action-btn connexion-action-btn-logout"
                  onClick={gererDeconnexion}
                  disabled={deconnexionEnCours}
                >
                  {deconnexionEnCours ? 'Deconnexion...' : 'Se deconnecter'}
                </button>
              ) : (
                <div className="connexion-actions">
                  <button
                    type="button"
                    className="connexion-action-btn connexion-action-btn-primary"
                    translate="no"
                    onClick={() => {
                      setInscriptionOuverte(false);
                      setFormulaireOuvert(true);
                    }}
                  >
                    Se connecter
                  </button>

                  <button
                    type="button"
                    className="connexion-action-btn connexion-action-btn-secondary"
                    onClick={() => {
                      setFormulaireOuvert(false);
                      setInscriptionOuverte(true);
                    }}
                  >
                    Creer un compte
                  </button>
                </div>
              )}
            </div>
          </div>
        </Container>
      </section>

      <FormulaireConnexion
        ouvert={formulaireOuvert}
        fermeture={() => setFormulaireOuvert(false)}
        onMotDePasseOublie={() => setMotDePasseOublieOuvert(true)}
      />

      <FormulaireInscription
        ouvert={inscriptionOuverte}
        fermeture={() => setInscriptionOuverte(false)}
      />

      <FormulaireMotDePasseOublie
        ouvert={motDePasseOublieOuvert}
        fermeture={() => setMotDePasseOublieOuvert(false)}
      />

      <FormulaireReinitialiserMotDePasse
        ouvert={reinitialiserMotDePasseOuvert}
        fermeture={() => setReinitialiserMotDePasseOuvert(false)}
        token={tokenReinitialisation}
      />
    </>
  );
};

export default Connexion;